import moment from "moment";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import grayZobu from "../../../../../../../assets/media/gray-zobu.svg";
import Warning from "../../../../../../../assets/media/svgs/Warning";
import { CurrencyContext } from "../../../../../../../contexts/currency";
import {
  Booking,
  CouponResponse,
} from "../../../../../../../definitions/booking";
import { PropertyRoom } from "../../../../../../../definitions/property";
import Flex from "../../../../../../structure/Flex";
import Icon from "../../../../../Icon";
import Portal from "../../../../../Portal";
import RoomSummary from "./RoomSummary";

interface MobileBookingSummaryProps {
  totalNights: number;
  bookingDetails: Booking;
  couponResponse: CouponResponse | undefined;
  rooms: PropertyRoom[];
  fetching: boolean;
  getRemoveRoomHandler: (roomRef: string) => () => void;
  handleBook: () => void;
  scrollToBooking?: () => void;
  scrollToOtherStays?: () => void;
  overTwoMonths: boolean;
  propertyName: string;
  showSimilarProperty?: boolean;
  onApplyCoupon: (code: string) => void;
  onRemoveCoupon: (code: string) => void;
  couponMessage: string;
  setCouponMessage: React.Dispatch<React.SetStateAction<string>>;
  guestCount?: number;
}

const MobileBookingSummary: React.FC<MobileBookingSummaryProps> = ({
  totalNights,
  bookingDetails,
  couponResponse,
  rooms,
  fetching,
  getRemoveRoomHandler,
  handleBook,
  scrollToBooking,
  scrollToOtherStays,
  overTwoMonths,
  propertyName,
  showSimilarProperty,
  onApplyCoupon,
  onRemoveCoupon,
  couponMessage,
  setCouponMessage,
  guestCount,
}) => {
  const { formatCurrency } = useContext(CurrencyContext);
  const [summaryVisible, setSummaryVisible] = useState<boolean>(false);
  const [showOtherStay, setShowOtherStay] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);

  const ref = useRef<HTMLElement | null>(null);
  const history = useHistory();

  const redirectToApp = () => {
    history.push("/app");
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowOtherStay(true);
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, []);

  const handleApplyCoupon = () => {
    if (couponCode.length > 0) {
      setCouponApplied(true);
      onApplyCoupon(couponCode);
      setCouponError(null);
    } else {
      setCouponError("Invalid coupon code");
      setCouponApplied(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setCouponApplied(false);
    setCouponError(null);
    onRemoveCoupon("");
    setCouponApplied(false);
    setCouponMessage("");
  };

  const getGuestWarningMessage = () => {
    if (!guestCount || !bookingDetails.rooms.length) return "";

    const selectedGuestCount = bookingDetails.rooms.reduce(
      (sum, bookingRoom) => {
        const roomCount = bookingRoom.count || 0;
        const matchingRoom = rooms.find(
          (room) => room._ref === bookingRoom._ref
        );
        const roomOccupancy = matchingRoom?.occupancy || 0;
        return sum + roomCount * roomOccupancy;
      },
      0
    );

    const remainingGuests = guestCount - selectedGuestCount;

    if (remainingGuests <= 0) return "";
    return `Select for ${Math.abs(remainingGuests)} more guest${
      Math.abs(remainingGuests) > 1 ? "s" : ""
    }`;
  };

  return (
    <Portal>
      <Flex col className="fixed bottom-0 left-0 z-10 w-full">
        <CSSTransition
          in={summaryVisible}
          classNames="fade-y"
          appear
          mountOnEnter
          unmountOnExit
          nodeRef={ref}
          timeout={500}
        >
          <section
            ref={ref}
            key="summary"
            className="p-6 border-t rounded-t-lg bg-light border-accent"
            style={{ maxHeight: "calc(100vh - 12rem)" }}
          >
            {/* {overTwoMonths ? (
              <div>
                <h3 className="text-2xl font-medium">
                  Planning well in advance?
                </h3>
                <p className="mt-6 text-base">
                  Bookings for dates more than 2 months away are exclusive to
                  our app.
                </p>
                <p className="mt-6 font-medium">
                  Download the Zostel App to unlock more such perks and
                  features.
                </p>
              </div>
            ) : ( */}
            <div>
              <Flex items="center" justify="between">
                <h3 className="text-2xl font-medium border-none bg-none">
                  Summary
                </h3>
                <button
                  className="focus:outline-none"
                  onClick={setSummaryVisible.bind(null, false)}
                >
                  <Icon className="font-medium">close</Icon>
                </button>
              </Flex>
              <div className="text-sm font-medium">
                {totalNights > 0
                  ? totalNights > 1
                    ? `${totalNights} nights`
                    : `${totalNights} night`
                  : "calculating nights"}{" "}
                <span className="text-text">starting from</span>{" "}
                {moment(bookingDetails.checkin).format("ddd D MMM, YYYY")}
              </div>
              {bookingDetails.rooms.length > 0 && couponResponse ? (
                <Flex col items="center" className="my-4">
                  <TransitionGroup className="w-full">
                    {bookingDetails.rooms.map((room) => {
                      let ref: string = room._ref.split("*")[0];
                      if (ref.endsWith("-")) {
                        ref = ref.substr(0, ref.length - 1);
                      }
                      return (
                        <CSSTransition
                          classNames="fade-x"
                          timeout={300}
                          key={room._ref}
                        >
                          <RoomSummary
                            key={room._ref}
                            count={room.count}
                            meta={{
                              ...rooms.find(
                                (_room) => _room._ref === room._ref
                              ),
                              ...couponResponse.rooms.find(
                                (_room: { _ref: string }) => _room._ref === ref
                              ),
                            }}
                            fetching={fetching}
                            totalNights={totalNights}
                            removeRoom={getRemoveRoomHandler(room._ref)}
                          />
                        </CSSTransition>
                      );
                    })}
                  </TransitionGroup>
                  <hr className="w-full my-4 h-[1px] border-dashed border-stroke-primary" />
                  <Flex
                    items="center"
                    justify="between"
                    className="w-full mb-2 text-lg font-normal text-dark"
                  >
                    <span>Tax</span>
                    {fetching ? (
                      <span className="font-medium text-subtitle">
                        Fetching ...
                      </span>
                    ) : (
                      <span>{formatCurrency(couponResponse.tax_amount)}</span>
                    )}
                  </Flex>
                  <Flex
                    items="center"
                    justify="between"
                    className="w-full mb-2 text-lg font-normal text-dark"
                  >
                    <span>Grand Total (tax incl.)</span>
                    {fetching ? (
                      <span className="font-medium text-subtitle">
                        Fetching ...
                      </span>
                    ) : (
                      <span>{formatCurrency(couponResponse.total_amount)}</span>
                    )}
                  </Flex>
                  <Flex
                    items="center"
                    justify="between"
                    className="w-full mb-6 text-lg font-medium text-green"
                  >
                    <span>Payable Now</span>
                    {fetching ? (
                      <span className="font-medium text-subtitle">
                        Fetching ...
                      </span>
                    ) : (
                      <span>
                        {formatCurrency(couponResponse.advance_amount)}
                      </span>
                    )}
                  </Flex>
                </Flex>
              ) : (
                <Flex col className="w-full" items="center" justify="center">
                  <img src={grayZobu} alt="zobu" className="h-40" />
                  <span className="mt-4 text-lg font-medium text-subtitle">
                    No room selected
                  </span>
                </Flex>
              )}
            </div>
            {/* )} */}
          </section>
        </CSSTransition>

        {showSimilarProperty && (
          <CSSTransition
            in={showOtherStay}
            appear
            timeout={1000}
            classNames="fade-y"
          >
            <section className="relative z-10 flex flex-col w-full pointer-events-auto">
              {showOtherStay && (
                <Flex
                  items="center"
                  justify="between"
                  className="px-4 py-2 rounded-t-lg bg-gradient-to-r from-orange to-pink-700 animate-slide-in "
                >
                  <Flex col className="text-sm font-medium text-white">
                    Other Stays Near {propertyName}
                  </Flex>
                  <button
                    className="px-4 py-1 text-sm font-medium tracking-wide text-center text-white border rounded-lg animate-pulse bg-dark focus:outline-none"
                    onClick={scrollToOtherStays}
                  >
                    Show
                  </button>
                </Flex>
              )}
            </section>
          </CSSTransition>
        )}
        <section className="relative z-10 flex flex-col w-full pointer-events-auto">
          <div className="px-4 py-2 bg-white animate-slide-in">
            {couponApplied ? (
              <Flex items="center" justify="between" className="w-full">
                {couponMessage === "invalid" ? (
                  <p className="text-sm text-red-500">
                    Invalid Coupon:{" "}
                    <span className="text-lg font-medium text-red-500">
                      {couponCode}
                    </span>
                  </p>
                ) : couponMessage === "valid" ? (
                  <p className="text-sm text-green-500 ">
                    Coupon Applied:{" "}
                    <span className="text-lg font-medium text-green-500">
                      {couponCode}
                    </span>
                  </p>
                ) : null}
                {couponMessage && (
                  <button
                    className="flex items-center text-red-500 focus:outline-none active:outline-none"
                    onClick={handleRemoveCoupon}
                  >
                    <Icon className="text-xl">close</Icon>
                    <span className="ml-2">Remove</span>
                  </button>
                )}
              </Flex>
            ) : showCouponInput ? (
              <div>
                <Flex items="center" className="gap-4">
                  <input
                    type="text"
                    className="flex-grow p-2 border rounded-lg"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                  <button
                    className="px-4 py-2 text-base font-medium border rounded-lg text-text"
                    onClick={handleApplyCoupon}
                  >
                    Apply
                  </button>
                </Flex>
                {couponError && (
                  <p className="text-sm text-red-500">{couponError}</p>
                )}
              </div>
            ) : (
              <Flex items="center" justify="between" className="w-full">
                <p className="text-sm font-medium text-primary">
                  Have a coupon code?
                </p>
                <button
                  className="px-4 py-1 text-sm font-medium tracking-wide text-center text-primary border rounded-lg shadow-lg border-orange focus:outline-none"
                  onClick={() => setShowCouponInput(true)}
                >
                  Apply
                </button>
              </Flex>
            )}
          </div>
        </section>

        <section className="relative z-10 w-full px-4 py-2 bg-white border-t pointer-events-auto border-accent">
          {bookingDetails.rooms.length > 0 ? (
            <Flex items="center" justify="between">
              <Flex col>
                <span className="text-sm font-medium text-subtitle">
                  Payable Now
                </span>
                {couponResponse ? (
                  <span className="font-medium text-text">
                    {formatCurrency(couponResponse.advance_amount)}
                  </span>
                ) : (
                  <span className="font-normal text-subtitle">
                    Fetching amount ...
                  </span>
                )}
              </Flex>
              <button
                className="px-4 py-3 text-sm font-medium tracking-wide text-center text-white rounded-xl focus:outline-none bg-orange"
                onClick={
                  summaryVisible
                    ? handleBook
                    : setSummaryVisible.bind(null, true)
                }
                disabled={summaryVisible && overTwoMonths}
              >
                {summaryVisible ? (
                  overTwoMonths ? (
                    <Link to="/app">Download app</Link>
                  ) : (
                    "Book Now"
                  )
                ) : (
                  "View Summary"
                )}
              </button>
            </Flex>
          ) : (
            <Flex items="center" justify="between">
              <Flex col className="font-medium text-subtitle">
                {overTwoMonths ? "" : "No room selected"}
              </Flex>
              <button
                className="px-4 py-3 text-sm font-medium tracking-wide text-center text-white rounded-xl focus:outline-none bg-orange"
                onClick={overTwoMonths ? redirectToApp : scrollToBooking}
              >
                {overTwoMonths ? "Download app" : "View Rooms"}
              </button>
            </Flex>
          )}
          {getGuestWarningMessage() && guestCount && (
            <div className="w-full  my-1 text-xs font-medium  text-center text-primary ">
              <Flex items="center" justify="center" className="gap-1">
                <Warning className="w-4 h-4" />
                <span>{getGuestWarningMessage()}</span>
              </Flex>
            </div>
          )}
        </section>
      </Flex>
    </Portal>
  );
};

export default MobileBookingSummary;
